/* eslint-disable @typescript-eslint/no-explicit-any */
import { put, select, takeEvery, all } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'
import { Map } from 'features/maps/types'
import { ActionTypes, layerConfigChange } from '@carto/kepler.gl/actions'
import { Layer } from '@carto/kepler.gl/layers'
import { setLegendSettings, showGlobalBuilderError, legendVisibilityChange, updateLayerState } from './builderSlice'
import { getLayerById } from './keplerGlSlice'
import { LegendSettings, SplitMaps } from 'features/builder/types'
import { migrateLegendSettings } from './legends'

export function* loadLegendSettings(currentMap: Map) {
  try {
    let legendSettings = currentMap.keplerMapConfig?.config?.legendSettings
    if (legendSettings) {
      legendSettings = migrateLegendSettings(legendSettings)
      yield put(setLegendSettings(legendSettings))
    }

    const layers = currentMap.keplerMapConfig?.config?.visState?.layers
    if (layers) {
      for (const layer of layers) {
        const legendHexColorMaps = layer.legendHexColorMaps || layer.legendSettings
        if (legendHexColorMaps) {
          yield put(updateLayerState({ layerId: layer.id, update: { legendHexColorMaps } }))
        }
      }
    }
  } catch (e: any) {
    yield put(showGlobalBuilderError(e.message))
  }
}

function* handleLegendVisibilityChange(action: PayloadAction<{ layerId: string; isVisible: boolean }>) {
  try {
    const { layerId, isVisible } = action.payload
    const layer = yield select(getLayerById, layerId)
    const splitMaps: SplitMaps = yield select((state) => state.keplerGl?.map?.visState?.splitMaps)

    if (splitMaps.length > 0) {
      const splitMapsVisibility = splitMaps.reduce((acc, sm) => acc || sm.layers[layerId], false)
      yield put(layerConfigChange(layer, { isVisible: splitMapsVisibility }))
    } else {
      yield put(layerConfigChange(layer, { isVisible }))
    }
  } catch (e: any) {
    yield put(showGlobalBuilderError(e.message))
  }
}

function* removeUnusedLegendSettings() {
  try {
    const legendSettings: LegendSettings | undefined = yield select((state) => state.builder.legendSettings)
    const layers: Layer[] = yield select((state) => state.keplerGl.map.visState.layers)

    if (!legendSettings) return

    const cleanedLayersSettings = Object.entries(legendSettings.layers).reduce(
      (remainingLayers, [layerId, layerSetting]) => {
        if (layers.some((l: Layer) => l.id === layerId)) {
          return { ...remainingLayers, [layerId]: layerSetting }
        }
        return remainingLayers
      },
      {}
    )

    yield put(setLegendSettings({ ...legendSettings, layers: cleanedLayersSettings }))
  } catch (e: any) {
    yield put(showGlobalBuilderError(e.message))
  }
}

function* watchMap() {
  yield takeEvery(ActionTypes.REMOVE_DATASET, removeUnusedLegendSettings)
  yield takeEvery(ActionTypes.REMOVE_LAYER, removeUnusedLegendSettings)
  yield takeEvery(legendVisibilityChange.type, handleLegendVisibilityChange)
}

export default function* legendSagas() {
  try {
    yield all([watchMap()])
  } catch (e: any) {
    //TODO
  }
}
